import { RefObject, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import {
  SchemaFilterType,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import { filtersSchema, schema } from '../variables/list';
import { useLazyGetCustomerBeneficiariesQuery } from '../../../customers/store/services/customers';
import { Beneficiary } from '../types';
import { OrganizationType } from '../../types';
import formatBeneficiariesList from '../helpers/format-beneficiaries-list';

import ROUTES from '../../../../routes';

const useBeneficiariesList = ({
  scrollRef,
  beneficiaries,
  type,
  customerId,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  beneficiaries?: Beneficiary[];
  type: OrganizationType;
  customerId?: string;
}) => {
  const [takeCount, setTakeCount] = useState(0);

  const [getBeneficiaries, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiariesQuery();

  const { filters, onFilterChange } = useQueryState(schema);

  const { getListAction, onFilter, setPage } = useGetList<Beneficiary>({
    getList: getBeneficiaries,
    onFilterChange,
    onSortChange: () => {},
    schema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const nextBeneficiaries = beneficiaries?.map((item) => ({
      ...item,
      url: generatePath(
        type === OrganizationType.INDIVIDUAL
          ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY
          : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY,
        { customerId: customerId as string, beneficiaryId: item.id },
      ),
    }));

    const data = formatBeneficiariesList(nextBeneficiaries as Beneficiary[]);

    return data;
  }, [beneficiaries]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = (offsetHeight - 51) / 46;

        setTakeCount(Math.ceil(take - 3));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (customerId && takeCount !== 0) {
      const filtersValues: any = {
        ...filters,
        organizationId: { value: [customerId], type: SchemaFilterType.STRING },
        take: { value: [takeCount.toString()], type: SchemaFilterType.STRING },
      };

      getListAction({ filters: filtersValues });
    }
  }, [customerId, takeCount]);

  return {
    formattedData,
    filters,
    onFilter,
    takeCount,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    setPage,
  };
};

export default useBeneficiariesList;
